import { WS_TAKEN_OVER, type ClientMessage, type ServerMessage } from '@tira/protocol';
import { wsUrl } from './origin.js';

/**
 * How long a socket must survive before it counts as a real connection.
 *
 * The backoff used to reset the moment `onopen` fired, which is only a signal
 * that the handshake worked. Anything that closes a socket straight after it
 * opens therefore retried at the floor delay forever, hammering the server and
 * showing the player a connection dropping once a second.
 */
const STABLE_MS = 4_000;

/**
 * Socket client. Reconnects with backoff and replays a resync request so a
 * dropped signal inside a hand resumes from the event log rather than
 * restarting it (PRD §8 — 60s grace, ghost seat).
 *
 * The client holds no authority: it sends intents and renders whatever view
 * the server hands back.
 */

type Listener = (m: ServerMessage) => void;
type StatusListener = (s: SocketStatus) => void;

/**
 * `superseded` is its own state on purpose: the connection is gone but nothing
 * is wrong, and the player is one tap from taking it back. Calling that
 * "reconnecting" is what made two tabs fight each other forever.
 */
export type SocketStatus =
  | 'idle'
  | 'connecting'
  | 'open'
  | 'reconnecting'
  | 'closed'
  | 'superseded';

export class GameSocket {
  private ws: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private statusListeners = new Set<StatusListener>();
  private token: string | null = null;
  private attempt = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private closedByUs = false;
  private openedAt: number | null = null;
  private queue: ClientMessage[] = [];

  /** Highest event seq seen per match, so a resync asks for exactly the gap. */
  readonly lastSeq = new Map<string, number>();

  status: SocketStatus = 'idle';

  connect(token: string): void {
    this.token = token;
    this.closedByUs = false;
    this.open();
  }

  private open(): void {
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) return;
    this.setStatus(this.attempt === 0 ? 'connecting' : 'reconnecting');
    const ws = new WebSocket(wsUrl());
    this.ws = ws;

    ws.onopen = () => {
      // Not `attempt = 0` here — see STABLE_MS. A handshake that succeeds and
      // is then closed again is not a connection, and treating it as one is
      // what turns any close-after-open into a hot loop.
      this.openedAt = Date.now();
      if (this.token) ws.send(JSON.stringify({ t: 'hello', token: this.token }));
      // Ask for anything missed while we were away, per live match.
      for (const [matchId, seq] of this.lastSeq)
        ws.send(JSON.stringify({ t: 'match.resync', matchId, sinceSeq: seq }));
      for (const m of this.queue.splice(0)) ws.send(JSON.stringify(m));
      this.setStatus('open');
    };

    ws.onmessage = (ev) => {
      let msg: ServerMessage;
      try {
        msg = JSON.parse(String(ev.data)) as ServerMessage;
      } catch {
        return;
      }
      if (msg.t === 'match.event' || msg.t === 'match.start') {
        const seq = msg.seq;
        const prev = this.lastSeq.get(msg.matchId) ?? -1;
        if (seq > prev) this.lastSeq.set(msg.matchId, seq);
      }
      if (msg.t === 'match.settle') this.lastSeq.delete(msg.matchId);
      for (const l of this.listeners) l(msg);
    };

    ws.onclose = (ev) => {
      this.ws = null;
      if (this.closedByUs) return this.setStatus('closed');
      /*
       * Another client took the account. Reconnecting would evict THEM, whose
       * client would reconnect and evict us, forever — so stand down and wait
       * for the player to say which screen they meant.
       */
      if (ev.code === WS_TAKEN_OVER) {
        this.attempt = 0;
        return this.setStatus('superseded');
      }
      // A socket that lived a while is a connection that dropped: start over
      // from the floor. One that died on arrival keeps counting, so a cause
      // that reproduces on every attempt backs off instead of hammering.
      if (this.openedAt !== null && Date.now() - this.openedAt >= STABLE_MS) this.attempt = 0;
      this.openedAt = null;
      this.setStatus('reconnecting');
      // Backoff caps at 5s: inside the 60s grace we want to be trying often.
      const delay = Math.min(5_000, 300 * 2 ** this.attempt++);
      this.timer = setTimeout(() => this.open(), delay);
    };

    ws.onerror = () => ws.close();
  }

  send(m: ClientMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(m));
    // Buffer intents made during a blip; they flush on the next open. Actions
    // carry the client's last seq, so the server rejects any that went stale.
    else if (this.queue.length < 32) this.queue.push(m);
  }

  /**
   * Take the account's socket back after another tab or device claimed it.
   * Deliberately manual — whichever screen the player touches last wins, and
   * nothing decides that for them.
   */
  resume(): void {
    if (this.status !== 'superseded' || this.closedByUs) return;
    this.attempt = 0;
    this.open();
  }

  on(l: Listener): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  onStatus(l: StatusListener): () => void {
    this.statusListeners.add(l);
    l(this.status);
    return () => this.statusListeners.delete(l);
  }

  private setStatus(s: SocketStatus): void {
    if (this.status === s) return;
    this.status = s;
    for (const l of this.statusListeners) l(s);
  }

  close(): void {
    this.closedByUs = true;
    if (this.timer) clearTimeout(this.timer);
    this.ws?.close();
    this.ws = null;
  }
}

export const socket = new GameSocket();
